import { __awaiter } from "tslib";
import { NodeViewWrapper } from '@tiptap/vue-3';
import { ref, defineComponent, watch, computed, onMounted, nextTick } from 'vue';
import { t } from '@getaccept/lib-shared-new/src/helpers/translation.helper';
import { DateTime } from 'luxon';
import { MergeKey, FieldType, MergeCategory, ProsemirrorType, FieldInputType, } from '../../types/enums';
import { ProsemirrorHelper } from '../../helpers/prosemirror.helper';
import { toolTipOptions } from '../../constants';
import { DateHelper } from '../../../../lib-shared-new/src/helpers/date.helper';
export default defineComponent({
    name: 'MergeTagView',
    components: {
        NodeViewWrapper,
    },
    props: {
        node: { type: Object, required: true },
        selected: { type: Boolean, default: false },
        editor: { type: Object, required: true },
        getPos: { type: Function, required: true },
    },
    setup(props) {
        const mergeTagPlaceholder = '{{';
        const options = ref(null);
        watch(() => props.editor.options.extensions, () => {
            const mergeTagExtension = props.editor.options.extensions.find(extension => extension.name === ProsemirrorType.MergeTag);
            if (!mergeTagExtension) {
                return;
            }
            options.value = mergeTagExtension.options;
        }, { immediate: true });
        const mergeKeyAttribute = computed(() => props.node.attrs.mergeKey);
        const categoryAttribute = computed(() => props.node.attrs.category);
        const tagId = computed(() => props.node.attrs.tagId);
        const customName = computed(() => props.node.attrs.customName);
        const field = computed(() => { var _a, _b; return (_b = (_a = options.value) === null || _a === void 0 ? void 0 : _a.fields) === null || _b === void 0 ? void 0 : _b.find(({ id }) => id === tagId.value); });
        const inputFieldType = computed(() => {
            var _a, _b, _c;
            return (_c = (_b = (_a = options.value) === null || _a === void 0 ? void 0 : _a.fields.find(({ inputSettings }) => inputSettings !== null)) === null || _b === void 0 ? void 0 : _b.inputSettings) === null || _c === void 0 ? void 0 : _c.type;
        });
        const documentValues = computed(() => { var _a, _b; return (_b = (_a = options.value) === null || _a === void 0 ? void 0 : _a.mergeValues) === null || _b === void 0 ? void 0 : _b.document; });
        const sender = computed(() => { var _a, _b; return (_b = (_a = options.value) === null || _a === void 0 ? void 0 : _a.mergeValues) === null || _b === void 0 ? void 0 : _b.sender; });
        const entity = computed(() => { var _a, _b; return (_b = (_a = options.value) === null || _a === void 0 ? void 0 : _a.mergeValues) === null || _b === void 0 ? void 0 : _b.entity; });
        const recipient = computed(() => { var _a, _b, _c; return (_c = (_b = (_a = options.value) === null || _a === void 0 ? void 0 : _a.mergeValues) === null || _b === void 0 ? void 0 : _b.recipients) === null || _c === void 0 ? void 0 : _c.find(({ id }) => recipientId.value === id); });
        const category = computed(() => { var _a; return (_a = field.value) === null || _a === void 0 ? void 0 : _a.category; });
        const fieldType = computed(() => { var _a; return (_a = field.value) === null || _a === void 0 ? void 0 : _a.type; });
        const mergeKey = computed(() => { var _a; return (_a = field.value) === null || _a === void 0 ? void 0 : _a.mergeKey; });
        const recipientId = computed(() => { var _a; return (_a = field.value) === null || _a === void 0 ? void 0 : _a.recipientId; });
        const mergeLabel = computed(() => String(customName.value || mergeKey.value).toLowerCase());
        const mergeTagType = computed(() => String(fieldType.value === FieldType.Custom ? FieldType.Custom : category.value).toLowerCase());
        const senderMergeKey = computed(() => {
            var _a;
            if (mergeKey.value === MergeKey.mobile) {
                return ((_a = sender.value) === null || _a === void 0 ? void 0 : _a[mergeKey.value]) ? mergeKey.value : MergeKey.phone;
            }
            return mergeKey.value;
        });
        const recipientMergeKey = computed(() => {
            var _a;
            if (mergeKey.value === MergeKey.mobile) {
                return ((_a = recipient.value) === null || _a === void 0 ? void 0 : _a[mergeKey.value]) ? mergeKey.value : MergeKey.phone;
            }
            return mergeKey.value;
        });
        const mergeValue = () => {
            var _a, _b, _c;
            if (fieldType.value === FieldType.Custom) {
                return field.value.value || field.value.customName;
            }
            switch (category.value || fieldType.value) {
                case MergeCategory.Recipient:
                    return recipientValue.value;
                case MergeCategory.Entity:
                    return ((_a = entity.value) === null || _a === void 0 ? void 0 : _a[mergeKey.value]) || `${category.value}.${mergeKey.value}`;
                case MergeCategory.Sender:
                    return ((_b = sender.value) === null || _b === void 0 ? void 0 : _b[senderMergeKey.value]) || `${category.value}.${mergeKey.value}`;
                case MergeCategory.Document:
                    return (documentValue.value ||
                        `${((_c = options.value) === null || _c === void 0 ? void 0 : _c.isDsr) ? t('room') : category.value}.${mergeKey.value}`);
                default:
                    return mergeTagPlaceholder;
            }
        };
        const documentValue = computed(() => {
            var _a;
            const value = (_a = documentValues.value) === null || _a === void 0 ? void 0 : _a[mergeKey.value];
            const isDateFieldInputType = inputFieldType.value === FieldInputType.Date;
            if (!value) {
                return '';
            }
            if (mergeKey.value === MergeKey.sendDate || mergeKey.value === MergeKey.expirationDate) {
                const date = DateTime.fromISO(value);
                return isDateFieldInputType
                    ? DateHelper.mediumDateLocale(date)
                    : DateHelper.mediumDateTime(date);
            }
            return value;
        });
        const recipientValue = computed(() => {
            var _a;
            return recipientNotFoundOrRole.value ||
                ((_a = recipient.value) === null || _a === void 0 ? void 0 : _a[recipientMergeKey.value]) ||
                `${recipient.value.firstName + recipient.value.lastName}.${mergeKey.value}`;
        });
        const recipientNotFoundOrRole = computed(() => {
            var _a, _b;
            if (!recipient.value) {
                return ((_a = options.value) === null || _a === void 0 ? void 0 : _a.isDsr)
                    ? t('missing_participant').toString()
                    : t('missing-recipient').toString();
            }
            if (!!recipient.value && !recipient.value.email && !((_b = recipient.value) === null || _b === void 0 ? void 0 : _b.mobile)) {
                return `${recipient.value.roleName}.${mergeKey.value}`;
            }
            return false;
        });
        const recipientTooltip = computed(() => recipientNotFoundOrRole.value ||
            `${recipient.value.firstName + recipient.value.lastName}.${mergeKey.value}`);
        const tooltipContent = computed(() => {
            var _a;
            if (fieldType.value === FieldType.Custom && field.value.value) {
                return field.value.customName;
            }
            if (!category.value && fieldType.value !== FieldType.Custom) {
                return mergeKey.value;
            }
            if (category.value === MergeCategory.Recipient && !recipient.value) {
                return ((_a = options.value) === null || _a === void 0 ? void 0 : _a.isDsr)
                    ? t('you_need_to_connect_a_new_participant').toString()
                    : t('you-need-to-connect-a-new-recipient').toString();
            }
            if (isMergeValueEmpty.value) {
                return `${t('value-missing').toString()} ${mergeValue()}`;
            }
            if (category.value === MergeCategory.Recipient) {
                return recipientTooltip.value;
            }
            return `${category.value}.${mergeKey.value}`;
        });
        const isRoleInTemplate = computed(() => {
            var _a;
            return ((_a = options.value) === null || _a === void 0 ? void 0 : _a.isTemplate) &&
                category.value === MergeCategory.Recipient &&
                !!recipient.value &&
                !recipient.value.email &&
                !recipient.value.mobile;
        });
        const isMergeValueEmpty = computed(() => {
            var _a, _b, _c;
            if (fieldType.value === FieldType.Custom) {
                return !field.value.value;
            }
            if (mergeKey.value === MergeKey.sendDate || isRoleInTemplate.value) {
                return false;
            }
            if (category.value === MergeCategory.Recipient) {
                return !recipient.value || !((_a = recipient.value) === null || _a === void 0 ? void 0 : _a.email) || !((_b = recipient.value) === null || _b === void 0 ? void 0 : _b[mergeKey.value]);
            }
            if (((_c = options.value) === null || _c === void 0 ? void 0 : _c.isDsr) && category.value === MergeCategory.Document) {
                return `${t('room')}.${mergeKey.value}`;
            }
            return mergeValue() === `${category.value}.${mergeKey.value}`;
        });
        onMounted(() => __awaiter(this, void 0, void 0, function* () {
            if (field.value) {
                return;
            }
            yield nextTick();
            openMenu();
        }));
        const selectMergeTag = () => {
            const selection = ProsemirrorHelper.selectNode(props.editor.state.tr, props.getPos());
            props.editor.view.dispatch(selection);
        };
        const openMenu = () => {
            if (!tagId.value) {
                selectMergeTag();
            }
            options.value.onOpenMenu();
        };
        return {
            toolTipOptions,
            tooltipContent,
            mergeKeyAttribute,
            categoryAttribute,
            mergeLabel,
            mergeTagType,
            openMenu,
            selectMergeTag,
            isMergeValueEmpty,
            mergeValue,
            mergeTagPlaceholder,
            tagId,
            customName,
            options,
            field,
            documentValues,
            sender,
        };
    },
});
